import { StringContext } from "../../core/StringContext";
import { StringModule } from "../../core/StringModule";
import { StringData } from "../../core/StringData";
import {
  SequenceState,
  SequenceEventData,
  SequenceProgressVars,
} from "../../models/slider/SequenceState";
import { StringObject } from "../../objects/StringObject";
import { EasingFunctionOutput } from "../../tools/EasingFunctionTool";
import { styleTxn } from "../../utils/style-txn";

interface TransitionState {
  fromStep: number;
  toStep: number;
  direction: 1 | -1;
  startTime: number;
  enteringDuration: number;
  leavingDuration: number;
}

interface StepElements {
  objects: StringObject[];
  enteringDuration: number;
  leavingDuration: number;
}

interface TriggerData {
  slider: string;
  step: number | "next" | "prev";
  loop: boolean;
}

interface GlobalSliderSettings {
  enteringDuration?: number;
  leavingDuration?: number;
  enteringEasing?: string;
  leavingEasing?: string;
  activeStep?: number;
}

type DurationKey = "enteringDuration" | "leavingDuration";
type EasingKey = "enteringEasing" | "leavingEasing";

export class StringSequence extends StringModule {
  private activeStep = new Map<string, number>();
  private leavingStep = new Map<string, number>();
  private transitions = new Map<string, TransitionState>();
  private elementIndex = new Map<string, StepElements>();
  private triggerElements = new Map<HTMLElement, TriggerData>();
  private globalSettings = new Map<string, GlobalSliderSettings>();
  private stateRegistered = new Set<string>();
  private lastEnteredStep = new Map<string, number>();
  private defaultDuration: number;
  private initialized = false;
  private static readonly ALL_STATES = Object.values(SequenceState);

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "sequence";
    this.defaultDuration = this.settings["sequence-duration"] ?? 600;

    this.attributesToMap = [
      ...this.attributesToMap,
      { key: "sequence", type: "string", fallback: "" },
      { key: "sequence-trigger", type: "string", fallback: "" },
      { key: "entering-easing", type: "string", fallback: "" },
      { key: "leaving-easing", type: "string", fallback: "" },
      { key: "entering-duration", type: "string", fallback: "" },
      { key: "leaving-duration", type: "string", fallback: "" },
      { key: "sequence-duration", type: "string", fallback: "" },
      { key: "active-step", type: "string", fallback: "" },
    ];
  }

  onInit(): void {
    super.onInit();
    this.events.on<SequenceEventData>("sequence", this.onSequenceEvent.bind(this));
    this.scanStandaloneTriggers();
  }

  private scanStandaloneTriggers(): void {
    const els = document.querySelectorAll<HTMLElement>(
      "[string-sequence-trigger]:not([string-inited])"
    );
    for (const el of Array.from(els)) {
      const trigger = el.getAttribute("string-sequence-trigger");
      const parsed = trigger ? this.parseTriggerKey(trigger) : null;
      if (parsed) {
        this.triggerElements.set(el, parsed);
        el.addEventListener("click", this.onTriggerClick);
      }
    }
  }

  private parseGlobalSettingsFromObject(object: StringObject): void {
    const get = (k: string) => object.getProperty<string>(k);
    const seq = get("sequence-duration");

    this.tryParseGlobalSetting(seq, "enteringDuration");
    this.tryParseGlobalSetting(seq, "leavingDuration");
    this.tryParseGlobalSetting(get("entering-duration"), "enteringDuration");
    this.tryParseGlobalSetting(get("leaving-duration"), "leavingDuration");
    this.tryParseGlobalSetting(get("entering-easing"), "enteringEasing");
    this.tryParseGlobalSetting(get("leaving-easing"), "leavingEasing");
    this.tryParseGlobalSetting(get("active-step"), "activeStep");
  }

  private tryParseGlobalSetting(attr: string | null, key: keyof GlobalSliderSettings): void {
    if (!attr) return;
    const match = attr.match(/^(.+)\[(.+)\]$/);
    if (!match) return;

    const [, slider, value] = match;
    const settings = this.globalSettings.get(slider) ?? {};
    this.globalSettings.set(slider, settings);

    (settings as Record<string, string | number>)[key] =
      key === "enteringEasing" || key === "leavingEasing" ? value : parseFloat(value);

    this.applyGlobalSettingsToExistingObjects(slider);
  }

  private applyGlobalSettingsToExistingObjects(slider: string): void {
    const global = this.globalSettings.get(slider);
    if (!global) return;

    for (const [key, group] of this.elementIndex) {
      const parsed = this.parseSequenceKey(key);
      if (parsed?.slider !== slider) continue;

      if (global.enteringDuration !== undefined) group.enteringDuration = global.enteringDuration;
      if (global.leavingDuration !== undefined) group.leavingDuration = global.leavingDuration;

      for (const obj of group.objects) this.resolveEasings(obj, key);
    }
  }

  private initializeSliders(): void {
    const sliders = new Set<string>();
    for (const key of this.elementIndex.keys()) {
      const p = this.parseSequenceKey(key);
      if (p) sliders.add(p.slider);
    }

    for (const slider of sliders) {
      if (this.activeStep.has(slider)) continue;
      const global = this.globalSettings.get(slider);
      let step = global?.activeStep ?? 0;
      if (!this.elementIndex.has(`${slider}[${step}]`)) step = 0;
      this.switchInstant(slider, step, 1);
    }
  }

  private tryApplyPendingActiveStep(slider: string): void {
    if (this.activeStep.has(slider)) return;
    const step = this.globalSettings.get(slider)?.activeStep;
    if (step !== undefined && this.elementIndex.has(`${slider}[${step}]`)) {
      this.switchInstant(slider, step, 1);
    }
  }

  override canConnect(object: StringObject): boolean {
    return object.keys.includes("sequence") || object.keys.includes("sequence-trigger");
  }

  override onObjectConnected(object: StringObject): void {
    super.onObjectConnected(object);
    this.parseGlobalSettingsFromObject(object);

    let sequence = object.getProperty<string>("sequence");
    const trigger = object.getProperty<string>("sequence-trigger");

    if (!sequence && trigger) {
      const p = this.parseTriggerKey(trigger);
      if (p && typeof p.step === "number") {
        sequence = `${p.slider}[${p.step}]`;
        object.setProperty("sequence", sequence);
      }
    }

    if (sequence) {
      const parsed = this.parseSequenceKey(sequence);
      if (parsed) {
        this.ensureStateEventRegistered(parsed.slider);
        let group = this.elementIndex.get(sequence);
        if (!group) {
          const { enteringDuration, leavingDuration } = this.resolveDurations(object, sequence);
          group = { objects: [], enteringDuration, leavingDuration };
          this.elementIndex.set(sequence, group);
        }
        group.objects.push(object);
        this.resolveEasings(object, sequence);

        const active = this.activeStep.get(parsed.slider);
        this.setState(
          object,
          active === parsed.step ? SequenceState.ACTIVE : SequenceState.DISABLED,
          active === parsed.step ? 1 : 0,
          1
        );
        this.tryApplyPendingActiveStep(parsed.slider);
      }
    }

    if (trigger) {
      const p = this.parseTriggerKey(trigger);
      if (p) {
        this.triggerElements.set(object.htmlElement, p);
        object.htmlElement.addEventListener("click", this.onTriggerClick);
      }
    }
  }

  private ensureStateEventRegistered(slider: string): void {
    if (this.stateRegistered.has(slider)) return;
    this.stateRegistered.add(slider);
    this.events.registerStateEvent?.(`sequence:active:${slider}`);
  }

  private parseTriggerKey(key: string): TriggerData | null {
    const m = key.match(/^(.+)\[(next|prev|\d+)(\|loop)?\]$/);
    if (!m) return null;
    const step = m[2] === "next" || m[2] === "prev" ? m[2] : parseInt(m[2], 10);
    return { slider: m[1], step, loop: m[3] === "|loop" };
  }

  private getMaxStep(slider: string): number {
    let max = -1;
    for (const key of this.elementIndex.keys()) {
      const p = this.parseSequenceKey(key);
      if (p?.slider === slider && p.step > max) max = p.step;
    }
    return max;
  }

  private resolveDuration(
    object: StringObject,
    slider: string,
    key: DurationKey,
    attrKey: string
  ): number {
    const attr = object.getProperty<string>(attrKey);
    const seqAttr = object.getProperty<string>("sequence-duration");
    const global = this.globalSettings.get(slider)?.[key];

    if (attr && !attr.includes("[")) {
      const v = parseFloat(attr);
      if (!isNaN(v)) return v;
    }
    if (seqAttr && !seqAttr.includes("[")) {
      const v = parseFloat(seqAttr);
      if (!isNaN(v)) return v;
    }
    return global ?? this.defaultDuration;
  }

  private resolveDurations(object: StringObject, sequence: string) {
    const slider = this.parseSequenceKey(sequence)?.slider ?? "";
    return {
      enteringDuration: this.resolveDuration(
        object,
        slider,
        "enteringDuration",
        "entering-duration"
      ),
      leavingDuration: this.resolveDuration(object, slider, "leavingDuration", "leaving-duration"),
    };
  }

  private resolveEasing(
    object: StringObject,
    slider: string,
    key: EasingKey,
    attrKey: string
  ): void {
    let easing = object.getProperty<string | EasingFunctionOutput>(attrKey);
    if (!easing || (typeof easing === "string" && easing.includes("["))) {
      easing = this.globalSettings.get(slider)?.[key] ?? this.settings["easing"] ?? "ease-out";
    }
    if (typeof easing === "string") {
      object.setProperty(attrKey, this.tools.easingFunction.process({ easing }));
    }
  }

  private resolveEasings(object: StringObject, sequence: string): void {
    const slider = this.parseSequenceKey(sequence)?.slider;
    if (!slider) return;
    this.resolveEasing(object, slider, "enteringEasing", "entering-easing");
    this.resolveEasing(object, slider, "leavingEasing", "leaving-easing");
  }

  override onObjectDisconnected(object: StringObject): void {
    super.onObjectDisconnected(object);

    const sequence = object.getProperty<string>("sequence");
    if (sequence) {
      const group = this.elementIndex.get(sequence);
      if (group) {
        const idx = group.objects.indexOf(object);
        if (idx !== -1) group.objects.splice(idx, 1);
        if (!group.objects.length) this.elementIndex.delete(sequence);
      }
    }

    if (this.triggerElements.has(object.htmlElement)) {
      object.htmlElement.removeEventListener("click", this.onTriggerClick);
      this.triggerElements.delete(object.htmlElement);
    }
  }

  private parseSequenceKey(key: string): { slider: string; step: number } | null {
    const m = key.match(/^(.+)\[(\d+)\]$/);
    return m ? { slider: m[1], step: parseInt(m[2], 10) } : null;
  }

  private onTriggerClick = (e: Event): void => {
    const data = this.triggerElements.get(e.currentTarget as HTMLElement);
    if (!data) return;

    const current = this.activeStep.get(data.slider) ?? 0;
    const max = this.getMaxStep(data.slider);
    let target: number, dir: 1 | -1;

    if (data.step === "next") {
      target = current + 1;
      dir = 1;
      if (!this.elementIndex.has(`${data.slider}[${target}]`)) {
        if (data.loop && max >= 0) target = 0;
        else return;
      }
    } else if (data.step === "prev") {
      target = current - 1;
      dir = -1;
      if (target < 0) {
        if (data.loop && max >= 0) target = max;
        else return;
      }
      if (!this.elementIndex.has(`${data.slider}[${target}]`)) return;
    } else {
      target = data.step;
      if (current === target) return;
      dir = target > current ? 1 : -1;
    }

    this.startTransition(data.slider, target, dir);
  };

  private onSequenceEvent(data: SequenceEventData): void {
    const { slider, step, transitionProgress, direction = 1, duration, instant } = data;
    const current = this.activeStep.get(slider);

    if (current === step && transitionProgress === undefined) return;

    if (transitionProgress !== undefined) {
      this.handleScrub(slider, step, transitionProgress, direction);
    } else if (instant) {
      this.switchInstant(slider, step, direction);
    } else {
      this.startTransition(slider, step, direction, duration);
    }
  }

  private startTransition(
    slider: string,
    toStep: number,
    direction: 1 | -1,
    customDuration?: number
  ): void {
    const fromStep = this.activeStep.get(slider);
    const prevLeaving = this.leavingStep.get(slider);

    this.ensureStateEventRegistered(slider);

    if (prevLeaving !== undefined && prevLeaving !== fromStep) {
      this.setStepState(slider, prevLeaving, SequenceState.DISABLED, 0, direction);
    }

    const enteringGroup = this.elementIndex.get(`${slider}[${toStep}]`);
    const leavingGroup =
      fromStep !== undefined ? this.elementIndex.get(`${slider}[${fromStep}]`) : null;

    if (fromStep !== undefined) this.leavingStep.set(slider, fromStep);
    this.activeStep.set(slider, toStep);
    this.emitActiveState(slider, toStep);

    const transition: TransitionState = {
      fromStep: fromStep ?? toStep,
      toStep,
      direction,
      startTime: this.data.time.now,
      enteringDuration: customDuration ?? enteringGroup?.enteringDuration ?? this.defaultDuration,
      leavingDuration: customDuration ?? leavingGroup?.leavingDuration ?? this.defaultDuration,
    };

    this.transitions.set(slider, transition);
    this.emitTransitionStart(slider, transition);
  }

  private handleScrub(slider: string, step: number, progress: number, direction: 1 | -1): void {
    this.transitions.delete(slider);
    const current = this.activeStep.get(slider);

    if (current !== step) {
      const prev = this.leavingStep.get(slider);
      if (prev !== undefined) this.setStepState(slider, prev, SequenceState.DISABLED, 0, direction);
      if (current !== undefined) this.leavingStep.set(slider, current);
      this.activeStep.set(slider, step);
      this.emitActiveState(slider, step);
    }

    const fromStep = this.leavingStep.get(slider) ?? current ?? step;
    this.applyProgress(slider, fromStep, step, progress, progress, direction);
  }

  private switchInstant(slider: string, step: number, direction: 1 | -1): void {
    this.transitions.delete(slider);
    const current = this.activeStep.get(slider);
    const leaving = this.leavingStep.get(slider);

    if (leaving !== undefined)
      this.setStepState(slider, leaving, SequenceState.DISABLED, 0, direction);
    if (current !== undefined && current !== step)
      this.setStepState(slider, current, SequenceState.DISABLED, 0, direction);

    this.activeStep.set(slider, step);
    this.leavingStep.delete(slider);
    this.setStepState(slider, step, SequenceState.ACTIVE, 1, direction);

    this.emitActiveState(slider, step);
    if (leaving !== undefined && leaving !== step)
      this.emitStepLeave(slider, leaving, direction, true);
    else if (current !== undefined && current !== step)
      this.emitStepLeave(slider, current, direction, true);
    this.emitStepEnter(slider, step, direction, true);

    const transition: TransitionState = {
      fromStep: current ?? step,
      toStep: step,
      direction,
      startTime: this.data.time.now,
      enteringDuration: 0,
      leavingDuration: 0,
    };
    this.emitTransitionStart(slider, transition);
    this.emitTransitionEnd(slider, step, current ?? step, direction, true);
  }

  private applyProgress(
    slider: string,
    fromStep: number,
    toStep: number,
    enteringProg: number,
    leavingProg: number,
    direction: 1 | -1
  ): void {
    const active = this.activeStep.get(slider)!;
    const leaving = this.leavingStep.get(slider);

    this.setStepState(
      slider,
      active,
      enteringProg >= 1 ? SequenceState.ACTIVE : SequenceState.ENTERING,
      enteringProg,
      direction
    );

    if (leaving !== undefined && leaving !== active) {
      if (leavingProg >= 1) {
        this.setStepState(slider, leaving, SequenceState.DISABLED, 0, direction);
        this.leavingStep.delete(slider);
        this.emitStepLeave(slider, leaving, direction, false);
      } else {
        this.setStepState(slider, leaving, SequenceState.LEAVING, leavingProg, direction);
      }
    }

    this.emitTransitionProgress(slider, fromStep, toStep, enteringProg, leavingProg, direction);

    if (enteringProg >= 1) {
      this.emitStepEnter(slider, active, direction, false);
    }
  }

  private setStepState(
    slider: string,
    step: number,
    state: SequenceState,
    progress: number,
    direction: 1 | -1
  ): void {
    const group = this.elementIndex.get(`${slider}[${step}]`);
    if (group) for (const obj of group.objects) this.setState(obj, state, progress, direction);
  }

  private setState(
    object: StringObject,
    state: SequenceState,
    rawProgress: number,
    direction: 1 | -1
  ): void {
    const el = object.htmlElement;
    const currentState = object.getProperty<SequenceState>("_state");
    const currentDir = object.getProperty<number>("_direction");

    const easing = object.getProperty<EasingFunctionOutput>(
      state === SequenceState.LEAVING ? "leaving-easing" : "entering-easing"
    );
    const progress = typeof easing === "function" ? easing(rawProgress) : rawProgress;

    if (currentState !== state) {
      el.classList.remove(...StringSequence.ALL_STATES);
      el.classList.add(state);
      object.setProperty("_state", state);
    }

    if (currentDir !== direction) {
      object.setProperty("_direction", direction);
      styleTxn.run(() =>
        styleTxn.setVars(el, { [SequenceProgressVars.DIRECTION]: direction.toString() })
      );
    }
  }

  onFrame(data: StringData): void {
    super.onFrame(data);

    if (!this.initialized) {
      this.initialized = true;
      this.initializeSliders();
    }

    for (const [slider, t] of this.transitions) {
      const elapsed = data.time.now - t.startTime;
      const enteringProg = Math.min(1, elapsed / t.enteringDuration);
      const leavingProg = Math.min(1, elapsed / t.leavingDuration);

      this.applyProgress(slider, t.fromStep, t.toStep, enteringProg, leavingProg, t.direction);

      if (enteringProg >= 1 && leavingProg >= 1) {
        this.emitTransitionEnd(slider, t.toStep, t.fromStep, t.direction, false);
        this.transitions.delete(slider);
      }
    }
  }

  private emitTransitionStart(slider: string, t: TransitionState): void {
    const payload = {
      slider,
      from: t.fromStep,
      to: t.toStep,
      direction: t.direction,
      enteringDuration: t.enteringDuration,
      leavingDuration: t.leavingDuration,
      startedAt: t.startTime,
    };
    this.events.emit("sequence:transition:start", payload);
    this.events.emit(`sequence:transition:start:${slider}`, payload);
  }

  private emitTransitionProgress(
    slider: string,
    from: number,
    to: number,
    entering: number,
    leaving: number,
    direction: 1 | -1
  ): void {
    const payload = { slider, from, to, entering, leaving, direction };
    this.events.emit("sequence:transition:progress", payload);
    this.events.emit(`sequence:transition:progress:${slider}`, payload);
  }

  private emitTransitionEnd(
    slider: string,
    to: number,
    from: number,
    direction: 1 | -1,
    instant: boolean
  ): void {
    const payload = { slider, from, to, direction, instant };
    this.events.emit("sequence:transition:end", payload);
    this.events.emit(`sequence:transition:end:${slider}`, payload);
  }

  private emitStepEnter(slider: string, step: number, direction: 1 | -1, instant: boolean): void {
    if (!instant && this.lastEnteredStep.get(slider) === step) return;
    this.lastEnteredStep.set(slider, step);
    const payload = { slider, step, direction, instant };
    this.events.emit("sequence:step:enter", payload);
    this.events.emit(`sequence:step:enter:${slider}`, payload);
  }

  private emitStepLeave(
    slider: string,
    step: number | null,
    direction: 1 | -1,
    instant: boolean
  ): void {
    if (step === null || step === undefined) return;
    const payload = { slider, step, direction, instant };
    this.events.emit("sequence:step:leave", payload);
    this.events.emit(`sequence:step:leave:${slider}`, payload);
  }

  private emitActiveState(slider: string, step: number): void {
    const payload = { slider, step };
    this.events.emit("sequence:active", payload);
    this.events.emit(`sequence:active:${slider}`, payload);
  }
}
